import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  businessApi,
  type BillingStatus,
  type BusinessFunnel,
  type BusinessInstanceRow,
  type BusinessOverview,
  type RangeKey,
} from '../api/client'
import {
  Alert, Badge, Button, Card, CardHeader, EmptyState, PageHeader, SegmentedControl, Skeleton, SortableTh,
  type BadgeTone,
} from '../components/ui'
import { useListState } from '../hooks/useListState'
import { sortRows } from '../lib/list-state'

const FUNNEL_RANGES: { label: string; value: RangeKey }[] = [
  { label: '30d', value: '30d' },
  { label: '90d', value: '90d' },
]

// Best first; matches the backend's group order.
const STATUS_TONE: Record<BillingStatus, BadgeTone> = {
  paying: 'success',
  trial: 'accent',
  overdue: 'warning',
  unbilled: 'danger',
  internal: 'neutral',
}

function fmtTRY(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '-'
  return `₺${n.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`
}

function apiError(e: unknown): string {
  return (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? String(e)
}

function fmtPct(n: number, decimals = 0): string {
  return `${n.toFixed(decimals)}%`
}

function StatCard({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <Card>
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${accent ?? 'text-gray-900'}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-gray-400">{sub}</p>}
    </Card>
  )
}

function StatusBadge({ status }: { status: BillingStatus }) {
  const { t } = useTranslation()
  return (
    <span title={t(`business.statusHint.${status}`)}>
      <Badge tone={STATUS_TONE[status]}>{t(`business.status.${status}`)}</Badge>
    </span>
  )
}

/** Says whether the revenue column can be trusted. Silence means Odoo answered. */
function OdooNotice({ odoo }: { odoo: BusinessOverview['odoo'] }) {
  const { t } = useTranslation()
  if (!odoo.configured) {
    return <Alert tone="warning" className="mb-6">{t('business.odooNotConfigured')}</Alert>
  }
  if (!odoo.error) return null
  return (
    <Alert tone="warning" className="mb-6">
      {odoo.fetched_at
        ? t('business.odooError', { error: odoo.error, time: new Date(odoo.fetched_at).toLocaleString() })
        : t('business.odooErrorNoData', { error: odoo.error })}
    </Alert>
  )
}

// Server-side acquisition→revenue funnel. Each stage shows its distinct-user
// count, the conversion from the previous stage, and a proportional bar.
function FunnelSection() {
  const { t } = useTranslation()
  const [range, setRange] = useState<RangeKey>('30d')
  const { data: funnel } = useQuery<BusinessFunnel>({
    queryKey: ['business-funnel', range],
    queryFn: () => businessApi.funnel(range),
    staleTime: 60_000,
  })
  if (!funnel) return null
  const steps = [
    { key: 'registered', label: t('business.funnelRegistered'), value: funnel.registered },
    { key: 'created', label: t('business.funnelCreatedInstance'), value: funnel.created_instance },
    { key: 'checkout', label: t('business.funnelStartedCheckout'), value: funnel.started_checkout },
    { key: 'paid', label: t('business.funnelPaid'), value: funnel.paid },
  ]
  const top = Math.max(funnel.registered, 1)
  return (
    <Card className="mt-6">
      <CardHeader
        title={t('business.funnelTitle')}
        description={t('business.funnelHint')}
        actions={
          <>
            <span className="text-xs text-gray-400">{t('business.funnelCanceled', { count: funnel.canceled })}</span>
            <SegmentedControl size="sm" value={range} onChange={setRange} options={FUNNEL_RANGES} label={t('business.rangeLabel')} />
          </>
        }
        className="mb-4"
      />
      <div className="space-y-3">
        {steps.map((s, i) => {
          const prev = i === 0 ? null : steps[i - 1].value
          const conv = prev && prev > 0 ? (s.value / prev) * 100 : null
          const width = Math.max((s.value / top) * 100, s.value > 0 ? 4 : 0)
          return (
            <div key={s.key}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-gray-700">{s.label}</span>
                <span className="text-gray-900 font-medium tabular-nums">
                  {s.value.toLocaleString()}
                  {conv !== null && <span className="ml-2 text-xs font-normal text-gray-400">{fmtPct(conv, 1)}</span>}
                </span>
              </div>
              <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full rounded-full bg-green-500" style={{ width: `${width}%` }} />
              </div>
            </div>
          )
        })}
      </div>
    </Card>
  )
}

type SortKey = 'subdomain' | 'mrr_try' | 'revenue_try' | 'cost_try' | 'margin_try'

export default function BusinessPage() {
  const { t } = useTranslation()
  // Margins open ascending so the instances losing money surface first.
  const { sortKey, sortDir, toggleSort } = useListState<SortKey>({
    sortKey: 'margin_try',
    sortDir: 'asc',
    defaultDirFor: (k) => (k === 'margin_try' || k === 'subdomain' ? 'asc' : 'desc'),
  })

  const { data, isLoading } = useQuery({
    queryKey: ['business-overview'],
    queryFn: () => businessApi.overview(),
    staleTime: 60_000,
  })

  // A customer instance with no sale behind it gets one opened here — the ones
  // that predate automatic sales, or whose automatic attempt failed.
  const queryClient = useQueryClient()
  const [notice, setNotice] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null)
  const openSale = useMutation({
    mutationFn: (id: string) => businessApi.openSale(id),
    onSuccess: (sale) => {
      setNotice({
        tone: 'success',
        text: t(sale.existing ? 'business.saleExisted' : 'business.saleOpened', { order: sale.order }),
      })
      queryClient.invalidateQueries({ queryKey: ['business-overview'] })
    },
    onError: (e) => setNotice({ tone: 'danger', text: t('business.openSaleFailed', { error: apiError(e) }) }),
  })

  const rows = useMemo(
    () => (data ? sortRows(data.instances, sortKey, sortDir) : []),
    [data, sortKey, sortDir],
  )
  const count = (status: BillingStatus) => data?.groups.find((g) => g.status === status)?.instances ?? 0
  const totals = data?.totals
  const marginPct = totals && totals.revenue_try > 0 ? (totals.margin_try / totals.revenue_try) * 100 : null

  return (
    <div className="p-4 sm:p-8">
      <PageHeader title={t('business.title')} description={t('business.subtitle')} />

      {data && <OdooNotice odoo={data.odoo} />}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard
          label={t('business.revenue')}
          value={totals ? fmtTRY(totals.revenue_try) : '-'}
          sub={data ? t('business.revenueHint', { count: count('paying') }) : ''}
          accent="text-green-700"
        />
        <StatCard
          label={t('business.cost')}
          value={totals ? fmtTRY(totals.cost_try) : '-'}
          sub={t('business.costHint')}
        />
        <StatCard
          label={t('business.margin')}
          value={totals ? fmtTRY(totals.margin_try) : '-'}
          sub={marginPct !== null ? fmtPct(marginPct) : ''}
          accent={totals && totals.margin_try < 0 ? 'text-red-600' : 'text-green-700'}
        />
        <StatCard
          label={t('business.trial')}
          value={totals ? fmtTRY(totals.trial_mrr_try + totals.overdue_mrr_try) : '-'}
          sub={data ? t('business.trialHint', { count: count('trial') + count('overdue') }) : ''}
          accent="text-brand-700"
        />
        <StatCard
          label={t('business.unbilled')}
          value={totals ? fmtTRY(totals.unbilled_cost_try) : '-'}
          sub={data ? t('business.unbilledHint', { count: count('unbilled') }) : ''}
          accent="text-red-600"
        />
      </div>

      <Card className="mb-6">
        <CardHeader title={t('business.groupsTitle')} className="mb-4" />
        {isLoading || !data ? (
          <Skeleton className="h-32" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                  <th className="pb-2 font-medium">{t('business.colStatus')}</th>
                  <th className="pb-2 font-medium text-right">{t('business.colInstances')}</th>
                  <th className="pb-2 font-medium text-right">{t('business.colMRR')}</th>
                  <th className="pb-2 font-medium text-right">{t('business.colRevenue')}</th>
                  <th className="pb-2 font-medium text-right">{t('business.colCost')}</th>
                  <th className="pb-2 font-medium text-right">{t('business.colMargin')}</th>
                </tr>
              </thead>
              <tbody>
                {data.groups.map((g) => (
                  <tr key={g.status} className="border-b border-gray-50">
                    <td className="py-2">
                      <StatusBadge status={g.status} />
                      <span className="ml-2 text-xs text-gray-400 hidden md:inline">{t(`business.statusHint.${g.status}`)}</span>
                    </td>
                    <td className="py-2 text-right text-gray-700">{g.instances}</td>
                    <td className="py-2 text-right text-gray-700">{g.mrr_try ? fmtTRY(g.mrr_try) : '-'}</td>
                    <td className="py-2 text-right text-gray-700">{fmtTRY(g.revenue_try)}</td>
                    <td className="py-2 text-right text-gray-700">{fmtTRY(g.cost_try)}</td>
                    <td className={`py-2 text-right font-medium ${g.margin_try < 0 ? 'text-red-600' : 'text-green-700'}`}>
                      {fmtTRY(g.margin_try)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <CardHeader title={t('business.instancesTitle')} className="mb-4" />
        {notice && <Alert tone={notice.tone} className="mb-4">{notice.text}</Alert>}
        {isLoading ? (
          <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : rows.length === 0 ? (
          <EmptyState title={t('business.noInstances')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                  <SortableTh label={t('business.colInstance')} sortKey="subdomain" current={sortKey} dir={sortDir} onSort={toggleSort} className="pb-2" />
                  <th className="pb-2 font-medium">{t('business.colStatus')}</th>
                  <th className="pb-2 font-medium">{t('business.colSubscription')}</th>
                  <th className="pb-2 font-medium">{t('business.colInvoice')}</th>
                  <th className="pb-2 font-medium">{t('business.colPrepaid')}</th>
                  <SortableTh label={t('business.colMRR')} sortKey="mrr_try" current={sortKey} dir={sortDir} onSort={toggleSort} align="right" className="pb-2 text-right" />
                  <SortableTh label={t('business.colRevenue')} sortKey="revenue_try" current={sortKey} dir={sortDir} onSort={toggleSort} align="right" className="pb-2 text-right" />
                  <SortableTh label={t('business.colCost')} sortKey="cost_try" current={sortKey} dir={sortDir} onSort={toggleSort} align="right" className="pb-2 text-right" />
                  <SortableTh label={t('business.colMargin')} sortKey="margin_try" current={sortKey} dir={sortDir} onSort={toggleSort} align="right" className="pb-2 text-right" />
                  <th className="pb-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r: BusinessInstanceRow) => (
                  <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2.5 pr-3">
                      <span className="font-medium text-gray-800">{r.subdomain}</span>
                      <div className="text-xs text-gray-400 truncate max-w-[200px]">{r.partner || r.owner_email || '-'}</div>
                    </td>
                    <td className="py-2.5"><StatusBadge status={r.status} /></td>
                    <td className="py-2.5 text-gray-600">{r.subscription || '-'}</td>
                    <td className="py-2.5 text-gray-600">{r.invoice_state ? t(`business.invoice.${r.invoice_state}`) : '-'}</td>
                    <td className="py-2.5 text-gray-600 tabular-nums">{r.prepaid_until || '-'}</td>
                    <td className="py-2.5 text-right text-gray-700">{r.mrr_try ? fmtTRY(r.mrr_try) : '-'}</td>
                    <td className="py-2.5 text-right text-gray-700">{fmtTRY(r.revenue_try)}</td>
                    <td className="py-2.5 text-right text-gray-700">{fmtTRY(r.cost_try)}</td>
                    <td className={`py-2.5 text-right font-medium ${r.margin_try < 0 ? 'text-red-600' : 'text-green-700'}`}>
                      {fmtTRY(r.margin_try)}
                    </td>
                    <td className="py-2.5 pl-3 text-right whitespace-nowrap">
                      {r.status === 'unbilled' && (
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={openSale.isPending && openSale.variables === r.id}
                          disabled={openSale.isPending}
                          onClick={() => openSale.mutate(r.id)}
                        >
                          {t('business.openSale')}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FunnelSection />
    </div>
  )
}
